import { useMemo, useState } from "react";
import { App, Button, Card, DatePicker, Form, InputNumber, Modal, Popconfirm, Select, Space, Table, Typography, Upload } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { PriceSnapshot } from "@glow-price/shared";
import { STOCK_STATUSES, formatPriceCNY } from "@glow-price/shared";

import { channelProductsApi, channelsApi, priceSnapshotsApi, type ImportSummary } from "../api/resources";
import { CrudDrawer } from "../components/CrudDrawer";
import { ImportResultModal } from "../components/ImportResultModal";
import { PageHeader } from "../components";

const STOCK_OPTIONS = Object.values(STOCK_STATUSES).map((v) => ({ value: v, label: v }));

export function PriceSnapshotsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [channelProductId, setChannelProductId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportChannelId, setExportChannelId] = useState<string | undefined>();
  const [exporting, setExporting] = useState(false);

  const importMutation = useMutation({
    mutationFn: (file: File) => priceSnapshotsApi.importCsv(file),
    onSuccess: (data) => {
      setImportResult(data);
      message.success(`导入完成 新增 ${data.inserted} 条`);
      qc.invalidateQueries({ queryKey: ["price-snapshots"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const cpQuery = useQuery({
    queryKey: ["channel-products", "all"],
    queryFn: () => channelProductsApi.list({ page: 1, pageSize: 500 }),
  });
  const cpOptions = (cpQuery.data?.items ?? []).map((cp) => ({
    value: cp.id,
    label: `${cp.channelProductName} (${cp.channelProductId})`,
  }));

  const channelsQuery = useQuery({
    queryKey: ["channels", "all"],
    queryFn: () => channelsApi.list({ page: 1, pageSize: 200 }),
    enabled: exportModalOpen,
  });

  async function onExportChannelTemplate() {
    if (!exportChannelId) return;
    const channel = channelsQuery.data?.items.find((c) => c.id === exportChannelId);
    if (!channel) {
      message.error("找不到所选渠道");
      return;
    }
    setExporting(true);
    try {
      const res = await channelProductsApi.list({
        channelId: exportChannelId,
        page: 1,
        pageSize: 500,
      });
      const rows = res.items;
      if (rows.length === 0) {
        message.warning("该渠道暂无渠道商品");
        return;
      }
      const header =
        "channel_code,sku_code,channel_product_name,list_price,sale_price,coupon_price,final_price,stock_status,captured_at,source";
      const ts = new Date().toISOString();
      const body = rows
        .map((cp) =>
          [
            channel.code,
            csvEsc(cp.channelProductId),
            csvEsc(cp.channelProductName),
            "",
            "",
            "",
            "",
            "in_stock",
            ts,
            "manual",
          ].join(","),
        )
        .join("\n");
      const csv = `${header}\n${body}\n`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `price-snapshots-${channel.code}-${ts.slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setExportModalOpen(false);
      message.success(`已导出 ${rows.length} 个 SKU 的模板`);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "导出失败");
    } finally {
      setExporting(false);
    }
  }

  const list = useQuery({
    queryKey: ["price-snapshots", channelProductId, page],
    queryFn: () =>
      priceSnapshotsApi.list({ channelProductId, page, pageSize: 30 }),
    enabled: !!channelProductId,
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<PriceSnapshot> & { capturedAt?: unknown }) => {
      const payload = {
        ...values,
        capturedAt:
          values.capturedAt && typeof values.capturedAt === "object"
            ? (values.capturedAt as dayjs.Dayjs).toISOString()
            : (values.capturedAt as string | undefined),
      };
      return priceSnapshotsApi.create(payload);
    },
    onSuccess: () => {
      message.success("价格已新增");
      qc.invalidateQueries({ queryKey: ["price-snapshots"] });
      setDrawerOpen(false);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => priceSnapshotsApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["price-snapshots"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <PageHeader
        title="价格快照"
        description={
          <>
            append-only 价格历史。CSV 列（认 <code>channel_product_id</code> 或{" "}
            <code>channel_code + sku_code</code>）：
            <code>
              channel_product_id, channel_code, sku_code, list_price, sale_price, coupon_price,
              final_price, stock_status, captured_at, source
            </code>
          </>
        }
        extra={
          <Space wrap>
            <Select
              placeholder="选择渠道商品"
              options={cpOptions}
              value={channelProductId}
              onChange={(v) => {
                setPage(1);
                setChannelProductId(v);
              }}
              style={{ width: 300 }}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
            <Button onClick={downloadTemplate}>下载模板</Button>
            <Button onClick={() => setExportModalOpen(true)}>按渠道导出</Button>
            <Upload
              accept=".csv"
              showUploadList={false}
              beforeUpload={(file) => {
                importMutation.mutate(file as File);
                return false;
              }}
            >
              <Button loading={importMutation.isPending}>导入 CSV</Button>
            </Upload>
            <Button type="primary" disabled={!channelProductId} onClick={() => setDrawerOpen(true)}>
              录入新价格
            </Button>
          </Space>
        }
      />

      <PriceMiniChart snapshots={list.data?.items ?? []} />

      <Table<PriceSnapshot>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: 30,
          total: list.data?.total ?? 0,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "采集时间",
            dataIndex: "capturedAt",
            width: 180,
            render: (v) => dayjs(v).format("YYYY-MM-DD HH:mm"),
          },
          { title: "标价", dataIndex: "listPrice", render: formatPriceCNY, width: 100 },
          { title: "活动价", dataIndex: "salePrice", render: formatPriceCNY, width: 100 },
          { title: "券后价", dataIndex: "couponPrice", render: formatPriceCNY, width: 100 },
          { title: "到手价", dataIndex: "finalPrice", render: formatPriceCNY, width: 100 },
          { title: "库存", dataIndex: "stockStatus", width: 120 },
          { title: "来源", dataIndex: "source", width: 100 },
          {
            title: "操作",
            width: 100,
            render: (_, row) => (
              <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                <Button type="link" danger>
                  删除
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />

      <CrudDrawer
        open={drawerOpen}
        title="录入新价格快照"
        initialValues={
          {
            channelProductId,
            stockStatus: STOCK_STATUSES.IN_STOCK,
            capturedAt: dayjs(),
            source: "manual",
          } as unknown as Partial<PriceSnapshot>
        }
        loading={saveMutation.isPending}
        onClose={() => setDrawerOpen(false)}
        onSubmit={(values) =>
          saveMutation.mutateAsync(values as unknown as Partial<PriceSnapshot>)
        }
      >
        {() => (
          <>
            <Form.Item name="channelProductId" label="渠道商品" rules={[{ required: true }]}>
              <Select options={cpOptions} disabled />
            </Form.Item>
            <Form.Item name="listPrice" label="标价">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="salePrice" label="活动价">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="couponPrice" label="券后价">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="finalPrice" label="到手价（留空时自动按 coupon||sale||list 计算）">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="stockStatus" label="库存">
              <Select options={STOCK_OPTIONS} />
            </Form.Item>
            <Form.Item name="capturedAt" label="采集时间">
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="source" label="来源">
              <Select
                options={[
                  { value: "manual", label: "manual 人工录入" },
                  { value: "crawler", label: "crawler 抓取" },
                  { value: "import", label: "import 批量导入" },
                ]}
              />
            </Form.Item>
          </>
        )}
      </CrudDrawer>
      <ImportResultModal result={importResult} onClose={() => setImportResult(null)} />

      <Modal
        title="按渠道导出预填模板"
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={onExportChannelTemplate}
        okText="导出 CSV"
        confirmLoading={exporting}
        okButtonProps={{ disabled: !exportChannelId }}
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
          选择一个渠道，会下载该渠道下全部 active 渠道商品的 CSV。channel_code /
          sku_code / 商品名已预填，运营只需在 list_price 等列填上当天采集到的价格，
          然后用上方"导入 CSV"上传。
        </Typography.Paragraph>
        <Select
          placeholder="选择渠道"
          options={(channelsQuery.data?.items ?? []).map((c) => ({
            value: c.id,
            label: `${c.name} (${c.code})`,
          }))}
          value={exportChannelId}
          onChange={(v) => setExportChannelId(v)}
          style={{ width: "100%" }}
          loading={channelsQuery.isLoading}
          showSearch
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
        />
      </Modal>
    </div>
  );
}

function csvEsc(v: string): string {
  // RFC 4180-style: wrap in quotes if contains comma / quote / newline; double inner quotes.
  if (/[",\n]/.test(v)) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

function downloadTemplate() {
  const sample = [
    "channel_product_id,channel_code,sku_code,list_price,sale_price,coupon_price,final_price,stock_status,captured_at,source",
    ",cdf_japan,9784567890123,890,,,720,in_stock,2026-05-31T10:00:00Z,manual",
    ",tmall_global,SKU-EL-ANR-100,1280,1150,,1080,in_stock,2026-05-31T10:00:00Z,manual",
  ].join("\n");
  const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `price-snapshots-template-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function PriceMiniChart({ snapshots }: { snapshots: PriceSnapshot[] }) {
  const data = useMemo(() => {
    if (snapshots.length === 0) return [];
    // newest first → oldest first for chart
    return [...snapshots]
      .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))
      .map((s) => ({
        d: s.capturedAt.slice(5, 10),
        price: s.finalPrice,
      }));
  }, [snapshots]);

  if (data.length < 2) return null;
  return (
    <Card size="small" style={{ marginBottom: 12 }} title="价格趋势">
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="psFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6d2839" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#6d2839" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 4" stroke="#e8e2dc" vertical={false} />
            <XAxis dataKey="d" tick={{ fontSize: 11, fill: "#8a8580" }} axisLine={{ stroke: "#e8e2dc" }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#8a8580" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `¥${v}`} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e8e2dc" }} formatter={(v: number) => [formatPriceCNY(v), "最终价"]} />
            <Area type="monotone" dataKey="price" stroke="#6d2839" strokeWidth={2} fill="url(#psFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
